/**
 * Site Analytics beacon — Phase 11.
 *
 * Drop-in hook for marketing / landing pages. Auto-fires a `pageview`
 * event to /api/site-analytics/track on mount AND whenever the
 * `location.pathname` changes (SPA-friendly).
 *
 * Privacy: cookieless. We DO generate a stable random session_id for
 * the duration of the tab (sessionStorage) so we can compute sessions
 * without dropping a tracking cookie.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { API_BASE } from "@/lib/api";

const SESSION_KEY = "_mc_site_session";

function getSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch { return null; }
}

function getUtmParams() {
  try {
    const sp = new URLSearchParams(window.location.search);
    return {
      source: sp.get("utm_source") || null,
      medium: sp.get("utm_medium") || null,
      campaign: sp.get("utm_campaign") || null,
    };
  } catch { return {}; }
}

export function useSiteAnalytics({ branchSlug } = {}) {
  const loc = useLocation();
  useEffect(() => {
    // Fire & forget — never block UX on the beacon. Use sendBeacon if
    // available so the request survives page-unload.
    const body = JSON.stringify({
      kind: "pageview",
      page_path: loc.pathname + (loc.search || ""),
      page_title: typeof document !== "undefined" ? document.title : null,
      referrer: typeof document !== "undefined" ? document.referrer : "",
      session_id: getSessionId(),
      branch_slug: branchSlug,
      ...getUtmParams(),
    });
    const url = `${API_BASE}/site-analytics/track`;
    try {
      if (typeof navigator !== "undefined" && navigator.sendBeacon) {
        const blob = new Blob([body], { type: "application/json" });
        navigator.sendBeacon(url, blob);
        return;
      }
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body, keepalive: true,
      }).catch(() => {});
    } catch { /* never throw from the beacon */ }
  }, [loc.pathname, loc.search, branchSlug]);
}
